/**
 * Event factories and helpers over event lists.
 */
import type { RuleSet } from '../rules/ruleset';
import { cloneRules } from '../rules/ruleset';
import type { EventSource, Teams } from '../types';
import type {
  EventMeta,
  EventOfType,
  GameCreatedEvent,
  GameEvent,
  GameEventType,
  PayloadOf,
} from './types';
import { SCHEMA_VERSION, UNDOABLE_EVENT_TYPES } from './types';
import type { MatchInfo } from '../match/match';

function cloneMatch(m: MatchInfo): MatchInfo {
  return {
    ...m,
    ...(m.tournament ? { tournament: { ...m.tournament } } : {}),
    ...(m.championship ? { championship: { ...m.championship } } : {}),
    ...(m.officials ? { officials: { ...m.officials } } : {}),
    ...(m.signOff ? { signOff: { ...m.signOff } } : {}),
  };
}

export type EventMetaInput = Omit<EventMeta, 'schemaVersion'>;

/** Builds a typed event. schemaVersion is stamped automatically. */
export function createEvent<T extends GameEventType>(
  type: T,
  payload: PayloadOf<T>,
  meta: EventMetaInput,
): EventOfType<T> {
  return {
    id: meta.id,
    gameId: meta.gameId,
    seq: meta.seq,
    periodNo: meta.periodNo,
    clockMs: meta.clockMs,
    at: meta.at,
    schemaVersion: SCHEMA_VERSION,
    source: { ...meta.source },
    ...(meta.groupId !== undefined ? { groupId: meta.groupId } : {}),
    type,
    payload,
  } as unknown as EventOfType<T>;
}

export interface CreateGameInput {
  id: string;
  gameId: string;
  rules: RuleSet;
  teams: Teams;
  at: number;
  source?: EventSource;
  match?: MatchInfo;
}

/** The first event of every game (seq 1). */
export function createGameEvent(input: CreateGameInput): GameCreatedEvent {
  return createEvent(
    'GAME_CREATED',
    {
      rules: cloneRules(input.rules),
      teams: { home: { ...input.teams.home }, away: { ...input.teams.away } },
      ...(input.match !== undefined ? { match: cloneMatch(input.match) } : {}),
    },
    {
      id: input.id,
      gameId: input.gameId,
      seq: 1,
      periodNo: 0,
      clockMs: input.rules.periodLengthSec * 1000,
      at: input.at,
      source: input.source ?? { adapter: 'system' },
    },
  );
}

/** Returns a new array sorted by seq (input is not mutated). */
export function sortEvents(events: readonly GameEvent[]): GameEvent[] {
  return events.slice().sort((a, b) => a.seq - b.seq);
}

export function nextSeq(events: readonly GameEvent[]): number {
  let max = 0;
  for (const e of events) if (e.seq > max) max = e.seq;
  return max + 1;
}

/** Ids targeted by any EVENT_VOIDED event. */
export function voidedIds(events: readonly GameEvent[]): Set<string> {
  const ids = new Set<string>();
  for (const e of events) if (e.type === 'EVENT_VOIDED') ids.add(e.payload.targetId);
  return ids;
}

/**
 * Events in seq order with voided events and the EVENT_VOIDED markers themselves removed.
 * This is the list every derivation folds over.
 */
export function effectiveEvents(events: readonly GameEvent[]): GameEvent[] {
  const voided = voidedIds(events);
  return sortEvents(events).filter((e) => e.type !== 'EVENT_VOIDED' && !voided.has(e.id));
}

export function isUndoable(event: GameEvent): boolean {
  return UNDOABLE_EVENT_TYPES.indexOf(event.type) !== -1;
}

/** The latest non-voided SCORE / TEAM_FOUL / FREE_THROW_MISSED / TIMEOUT / SHOT_CLOCK_RESET, if any. */
export function findUndoTarget(events: readonly GameEvent[]): GameEvent | undefined {
  const voided = voidedIds(events);
  const sorted = sortEvents(events);
  for (let i = sorted.length - 1; i >= 0; i--) {
    const e = sorted[i];
    if (e !== undefined && isUndoable(e) && !voided.has(e.id)) return e;
  }
  return undefined;
}

/**
 * Everything one UNDO voids: the undo target plus every other non-voided event of its group
 * (same groupId), in seq order. Empty when there is nothing to undo.
 */
export function findUndoTargets(events: readonly GameEvent[]): GameEvent[] {
  const target = findUndoTarget(events);
  if (target === undefined) return [];
  const gid = target.groupId;
  if (gid === undefined) return [target];
  const voided = voidedIds(events);
  return sortEvents(events).filter(
    (e) => e.groupId === gid && e.type !== 'EVENT_VOIDED' && !voided.has(e.id),
  );
}
